/**
 * AP-10 — Einhaengepunkt der Views D (FM-WIN-11/12/13/18/19), gleiche
 * Lazy-Konvention wie `mountViewsB`/`mountViewsC`.
 *
 * Zusaetzlich haengt hier der Simulationsmodus: der Worker muss wissen, ob ein
 * Sendeauftrag an den Adapter geht oder in den Empfangspfad zurueckgeschleift
 * wird (FM-CON-09, FM-X-05).
 */

import { createCanopenDetailWindow } from './CanopenDetail';
import { createEngineSimulator, type EngineSimulatorHandle } from './EngineSimulator';
import { createIsobusDetailWindow } from './IsobusDetail';
import { createIsobusEventLog, type IsobusEventLogHandle } from './IsobusEventLog';
import { createTxMessageView, type TxMessageHandle } from './TxMessage';
import type { DetailWindowHandle } from './detailWindow';
import type { CanConnection } from '../app/connection';
import type { AppStore } from '../app/store';
import type { DetailWatch } from '../worker/protocol';

/**
 * PGN aus einem 29-Bit-Identifier — dieselbe Regel wie `Isobus::DecodeId`:
 * EDP/DP bleiben Teil des PGN, das PS-Byte nur fuer PDU2 (PF >= 0xF0).
 */
function pgnOf(id: number): number {
  const pf = (id >>> 16) & 0xff;
  const page = (id >>> 24) & 0x03;
  const base = (page << 16) | (pf << 8);
  return pf < 0xf0 ? base : base | ((id >>> 8) & 0xff);
}

export function mountViewsD(store: AppStore, connection: CanConnection): void {
  let txMessage: TxMessageHandle | null = null;
  let engineSimulator: EngineSimulatorHandle | null = null;
  let isobusEventLog: IsobusEventLogHandle | null = null;

  store.on('view:opened', (event) => {
    switch (event.kind) {
      case 'tx-message':
        txMessage ??= createTxMessageView(store, connection);
        txMessage.show();
        return;
      case 'engine-simulator':
        engineSimulator ??= createEngineSimulator(store, connection);
        engineSimulator.show();
        return;
      // Nachtrag der Synchronisation: dieser ViewKind hatte kein Arbeitspaket.
      case 'isobus-event-log':
        isobusEventLog ??= createIsobusEventLog(store, connection);
        isobusEventLog.show();
        return;
      default:
        return; // andere Views gehoeren AP-7/AP-8/AP-9/AP-11
    }
  });

  // --- Detailfenster ------------------------------------------------------
  const windows = new Map<string, DetailWindowHandle>();

  function watchKey(watch: DetailWatch): string {
    return `${watch.kind}:${watch.key}`;
  }

  function publishWatches(): void {
    const watches: DetailWatch[] = [];
    for (const key of windows.keys()) {
      const [kind, value] = key.split(':');
      watches.push({ kind: kind as DetailWatch['kind'], key: Number(value) });
    }
    void connection.setDetailWatch(watches);
  }

  store.on('frame:activated', (event) => {
    // Nur der Dekoder, der gerade aktiv ist, bekommt ein Detailfenster — so
    // macht es auch der Doppelklick im Original.
    const flags = store.decodeFlags;
    const extended = event.id > 0x7ff;
    let watch: DetailWatch | null = null;
    if (extended && flags.isobusEnabled) watch = { kind: 'isobus', key: pgnOf(event.id) };
    else if (!extended && flags.canopenEnabled) watch = { kind: 'canopen', key: event.id & 0x7ff };
    if (watch === null) return;

    const key = watchKey(watch);
    let window = windows.get(key);
    if (window === undefined) {
      window =
        watch.kind === 'isobus'
          ? createIsobusDetailWindow(watch.key, '')
          : createCanopenDetailWindow(watch.key, '');
      windows.set(key, window);
      publishWatches();
    }
    window.show();
  });

  connection.on('detail', (event) => {
    const window = windows.get(watchKey(event.watch));
    if (window === undefined) return;
    if (event.title !== '') {
      window.setTitle(
        event.watch.kind === 'isobus'
          ? `${event.title} (PGN 0x${event.watch.key.toString(16).toUpperCase().padStart(6, '0')})`
          : `${event.title} (COB-ID 0x${event.watch.key.toString(16).toUpperCase().padStart(3, '0')})`,
      );
    }
    window.update({
      dlc: event.dlc,
      data: event.data,
      summary: event.summary,
      fields: event.fields,
    });
  });

  // --- Simulationsmodus ---------------------------------------------------
  let simulationMode = store.settings.simulationMode;
  void connection.setSimulationMode(simulationMode);
  store.on('settings:changed', (event) => {
    if (event.settings.simulationMode === simulationMode) return;
    simulationMode = event.settings.simulationMode;
    void connection.setSimulationMode(simulationMode);
  });
}
